import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { GetAdminHelpdeskIssuesParams, Organization, Topic } from '../../client/interfaces';
import client from '../../client/client';
import { useAuth } from '../AuthContext/AuthContext';
import { emptyGetAdminHelpdeskIssuesParams } from '../../screens/Admin/AdminHelpdeskIssueListScreen';

interface AdminHelpdeskContext {
  topics: Topic[];
  setTopics: Dispatch<SetStateAction<Topic[]>>;
  organizations: Organization[];
  setOrganizations: Dispatch<SetStateAction<Organization[]>>;
  refreshTopicsAndOrganizations: () => Promise<{ topics: Topic[]; organizations: Organization[] }>;
  helpdeskParams: GetAdminHelpdeskIssuesParams;
  setHelpdeskParams: Dispatch<SetStateAction<GetAdminHelpdeskIssuesParams>>;
  helpdeskTotalCount: number;
  setHelpdeskTotalCount: Dispatch<SetStateAction<number>>;
}

const Context = createContext<AdminHelpdeskContext>({
  topics: [],
  setTopics: () => {},
  organizations: [],
  setOrganizations: () => {},
  refreshTopicsAndOrganizations: async () => ({ topics: [], organizations: [] }),
  helpdeskParams: emptyGetAdminHelpdeskIssuesParams,
  setHelpdeskParams: () => {},
  helpdeskTotalCount: 0,
  setHelpdeskTotalCount: () => {},
});

export const useAdminHelpdesk = () => useContext(Context);

export const AdminHelpdeskProvider: FC<PropsWithChildren> = ({ children }) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [helpdeskParams, setHelpdeskParams] = useState<GetAdminHelpdeskIssuesParams>(
    emptyGetAdminHelpdeskIssuesParams,
  );
  const [helpdeskTotalCount, setHelpdeskTotalCount] = useState(0);

  const refreshTopicsAndOrganizations = async () => {
    try {
      const [topicsRes, organizationsRes] = await Promise.all([
        client.getAdminHelpdeskTopics(),
        client.getAdminHelpdeskOrganizations(),
      ]);
      const newTopics = topicsRes.map((t) => ({
        id: t.id,
        name: t.name,
        translationValue: t.name,
        isActive: t.isActive,
      }));
      const newOrganizations = organizationsRes.map((o) => ({
        id: o.id,
        name: o.name,
        isActive: o.isActive,
      }));
      setTopics(newTopics);
      setOrganizations(newOrganizations);
      return { topics: newTopics, organizations: newOrganizations };
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch helpdesk topics/organizations');
      return { topics: [], organizations: [] };
    }
  };

  const value: AdminHelpdeskContext = {
    topics,
    setTopics,
    organizations,
    setOrganizations,
    refreshTopicsAndOrganizations,
    helpdeskParams,
    setHelpdeskParams,
    helpdeskTotalCount,
    setHelpdeskTotalCount,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
